import { useState } from "react";
import { useDesktopStore } from "../store.ts";

type ProposalType =
	| "team_name"
	| "relocation"
	| "rule_change"
	| "trade_review"
	| "expansion"
	| "other";

type ProposalStatus = "open" | "passed" | "rejected" | "pending_payment";

type Vote = {
	address: string;
	inFavor: boolean;
	at: number;
};

type Proposal = {
	id: string;
	type: ProposalType;
	title: string;
	description: string;
	proposedBy: string;
	/** tid of the team this affects, if any */
	tid?: number;
	city?: string;
	currentValue?: string;
	proposedValue?: string;
	status: ProposalStatus;
	votesFor: number;
	votesAgainst: number;
	totalOwners: number;
	votes: Vote[];
	createdAt: number;
	expiresAt: number;
};

const TYPE_LABELS: Record<ProposalType, string> = {
	team_name: "Team Name",
	relocation: "Relocation",
	rule_change: "Rule Change",
	trade_review: "Trade Review",
	expansion: "Expansion",
	other: "Other",
};

const STATUS_LABELS: Record<ProposalStatus, { label: string; color: string }> = {
	open: { label: "Voting Open", color: "#3b82f6" },
	passed: { label: "Passed", color: "#10b981" },
	rejected: { label: "Rejected", color: "#ef4444" },
	pending_payment: { label: "Pending Fee", color: "#f59e0b" },
};

const FRANCHISE_CITIES: Record<number, string> = {
	0: "Atlanta", 1: "Baltimore", 2: "Boston", 3: "Chicago", 4: "Cincinnati",
	5: "Cleveland", 6: "Dallas", 7: "Denver", 8: "Detroit", 9: "Houston",
	10: "Las Vegas", 11: "Los Angeles", 12: "Mexico City", 13: "Miami",
	14: "Minneapolis", 15: "Montreal", 16: "New York", 17: "Philadelphia",
	18: "Phoenix", 19: "Pittsburgh", 20: "Portland", 21: "Sacramento",
	22: "San Diego", 23: "San Francisco", 24: "Seattle", 25: "St. Louis",
	26: "Tampa Bay", 27: "Toronto", 28: "Vancouver", 29: "Washington",
};

/** Placeholder proposals - in production these come from chain events or an API. */
const DEMO_PROPOSALS: Proposal[] = [
	{
		id: "gov-001",
		type: "team_name",
		title: 'Atlanta - Name Proposal: "Hammers"',
		description:
			'The owner of the Atlanta franchise proposes the team name "Hammers". Requires majority approval from all other franchise owners.',
		proposedBy: "0xa8F6...aa818",
		tid: 0,
		city: "Atlanta",
		currentValue: "(none - unclaimed)",
		proposedValue: "Hammers",
		status: "open",
		votesFor: 0,
		votesAgainst: 0,
		totalOwners: 30,
		votes: [],
		createdAt: Date.now() - 3600_000,
		expiresAt: Date.now() + 6 * 24 * 3600_000,
	},
];

const VoteBar = ({ yes, no, total }: { yes: number; no: number; total: number }) => {
	const pct = total > 0 ? Math.round((yes / total) * 100) : 0;
	const needed = Math.ceil((total - 1) / 2); // majority of OTHER owners
	return (
		<div style={{ marginTop: 6 }}>
			<div
				style={{
					height: 8,
					borderRadius: 4,
					background: "var(--border)",
					overflow: "hidden",
					position: "relative",
				}}
			>
				<div
					style={{
						width: `${pct}%`,
						height: "100%",
						background: pct >= 50 ? "#10b981" : "#3b82f6",
						transition: "width 0.3s",
					}}
				/>
				{/* Threshold marker at 50% */}
				<div
					style={{
						position: "absolute",
						left: "50%",
						top: 0,
						bottom: 0,
						width: 2,
						background: "#fff",
						opacity: 0.5,
					}}
				/>
			</div>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					fontSize: "0.78em",
					color: "var(--muted)",
					marginTop: 3,
				}}
			>
				<span>
					{yes} For · {no} Against
				</span>
				<span>Needs {needed} of {total - 1} owner votes</span>
			</div>
		</div>
	);
};

export const Governance = () => {
	const connectedAddress = useDesktopStore((s) => s.connectedAddress);
	const cryptoMode = useDesktopStore((s) => s.cryptoMode);
	const claimedTeam = useDesktopStore((s) => s.claimedTeam);
	const pushToast = useDesktopStore((s) => s.pushToast);

	const [proposals, setProposals] = useState<Proposal[]>(DEMO_PROPOSALS);
	const [filter, setFilter] = useState<ProposalType | "all">("all");
	const [statusFilter, setStatusFilter] = useState<ProposalStatus | "all">("all");

	const canVote = cryptoMode && claimedTeam != null && connectedAddress != null;

	const castVote = (id: string, inFavor: boolean) => {
		if (!canVote || !connectedAddress) {
			pushToast({ text: "You must own a franchise to vote.", type: "error" });
			return;
		}
		setProposals((prev) =>
			prev.map((p) => {
				if (p.id !== id) return p;
				const alreadyVoted = p.votes.some(
					(v) => v.address.toLowerCase() === connectedAddress.toLowerCase(),
				);
				if (alreadyVoted) {
					pushToast({ text: "You have already voted on this proposal.", type: "error" });
					return p;
				}
				const newVote: Vote = {
					address: connectedAddress,
					inFavor,
					at: Date.now(),
				};
				const votes = [...p.votes, newVote];
				const votesFor = votes.filter((v) => v.inFavor).length;
				const votesAgainst = votes.filter((v) => !v.inFavor).length;
				const needed = Math.ceil((p.totalOwners - 1) / 2);
				const status: ProposalStatus =
					votesFor >= needed ? "passed" :
					votesAgainst >= needed ? "rejected" : "open";
				pushToast({
					text: inFavor
						? `Voted FOR: "${p.title}"`
						: `Voted AGAINST: "${p.title}"`,
					type: "info",
				});
				return { ...p, votes, votesFor, votesAgainst, status };
			}),
		);
	};

	const filtered = proposals.filter(
		(p) =>
			(filter === "all" || p.type === filter) &&
			(statusFilter === "all" || p.status === statusFilter),
	);

	return (
		<>
			<h1 className="page-title">League Governance</h1>
			<p className="page-sub">
				On-chain democratic governance for the WBBL. Franchise owners
				vote on team names, relocations, rule changes, trades requiring review,
				and expansion. Proposals pass by majority of all active franchise owners.
				Name changes and relocations require an EMBR fee and pass a vote before
				the NFT metadata is updated.
			</p>

			{!cryptoMode ? (
				<div
					className="panel"
					style={{ padding: "14px 18px", marginBottom: 16, borderLeft: "3px solid #f59e0b" }}
				>
					<p style={{ margin: 0, fontSize: "0.9em" }}>
						<strong>Desktop mode</strong> — Governance is live in the WBBL
						(browser/crypto mode). Here you see a preview of how the
						voting system works.
					</p>
				</div>
			) : !canVote ? (
				<div
					className="panel"
					style={{ padding: "14px 18px", marginBottom: 16, borderLeft: "3px solid #3b82f6" }}
				>
					<p style={{ margin: 0, fontSize: "0.9em" }}>
						You are browsing as a spectator. Claim a franchise to vote on
						proposals.
					</p>
				</div>
			) : (
				<div
					className="panel"
					style={{ padding: "14px 18px", marginBottom: 16, borderLeft: "3px solid #10b981" }}
				>
					<p style={{ margin: 0, fontSize: "0.9em" }}>
						<strong>Owner:</strong>{" "}
						{FRANCHISE_CITIES[claimedTeam!.tid]} franchise ·{" "}
						{connectedAddress?.slice(0, 6)}…{connectedAddress?.slice(-4)} — you
						can vote on open proposals.
					</p>
				</div>
			)}

			{/* Filters */}
			<div
				style={{
					display: "flex",
					gap: 8,
					marginBottom: 14,
					flexWrap: "wrap",
					alignItems: "center",
				}}
			>
				<span style={{ fontSize: "0.82em", color: "var(--muted)" }}>Type:</span>
				{(["all", "team_name", "relocation", "rule_change", "trade_review", "expansion", "other"] as const).map(
					(t) => (
						<button
							key={t}
							type="button"
							className={`btn ${filter === t ? "btn-primary" : "btn-secondary"}`}
							style={{ fontSize: "0.78em", padding: "3px 9px" }}
							onClick={() => setFilter(t)}
						>
							{t === "all" ? "All" : TYPE_LABELS[t]}
						</button>
					),
				)}
				<span style={{ fontSize: "0.82em", color: "var(--muted)", marginLeft: 8 }}>
					Status:
				</span>
				{(["all", "open", "passed", "rejected"] as const).map((s) => (
					<button
						key={s}
						type="button"
						className={`btn ${statusFilter === s ? "btn-primary" : "btn-secondary"}`}
						style={{ fontSize: "0.78em", padding: "3px 9px" }}
						onClick={() => setStatusFilter(s as any)}
					>
						{s === "all" ? "All" : STATUS_LABELS[s as ProposalStatus].label}
					</button>
				))}
			</div>

			{/* Proposal list */}
			{filtered.length === 0 ? (
				<div className="panel">
					<div className="panel-body">
						<p className="muted">No proposals match the current filter.</p>
					</div>
				</div>
			) : null}

			{filtered.map((p) => {
				const statusInfo = STATUS_LABELS[p.status];
				const myVote = connectedAddress
					? p.votes.find(
							(v) =>
								v.address.toLowerCase() === connectedAddress.toLowerCase(),
						)
					: undefined;
				const daysLeft = Math.max(
					0,
					Math.round((p.expiresAt - Date.now()) / 86_400_000),
				);

				return (
					<section className="panel" key={p.id} style={{ marginBottom: 14 }}>
						<div className="panel-header">
							<span>
								<span
									style={{
										fontSize: "0.75em",
										background: "var(--bg-secondary)",
										borderRadius: 4,
										padding: "2px 6px",
										marginRight: 8,
									}}
								>
									{TYPE_LABELS[p.type]}
								</span>
								{p.title}
							</span>
							<span
								className="pill"
								style={{ background: statusInfo.color, color: "#fff" }}
							>
								{statusInfo.label}
							</span>
						</div>
						<div className="panel-body">
							<p style={{ marginTop: 0 }}>{p.description}</p>

							{(p.currentValue || p.proposedValue) ? (
								<div
									style={{
										display: "flex",
										gap: 16,
										marginBottom: 10,
										fontSize: "0.88em",
									}}
								>
									{p.currentValue ? (
										<span>
											<span className="muted">Current: </span>
											<strong>{p.currentValue}</strong>
										</span>
									) : null}
									{p.proposedValue ? (
										<span>
											<span className="muted">Proposed: </span>
											<strong style={{ color: "#3b82f6" }}>
												{p.proposedValue}
											</strong>
										</span>
									) : null}
								</div>
							) : null}

							<VoteBar
								yes={p.votesFor}
								no={p.votesAgainst}
								total={p.totalOwners}
							/>

							<div
								style={{
									display: "flex",
									gap: 10,
									marginTop: 12,
									alignItems: "center",
									flexWrap: "wrap",
								}}
							>
								{p.status === "open" && canVote ? (
									myVote ? (
										<span
											style={{
												fontSize: "0.85em",
												color: myVote.inFavor ? "#10b981" : "#ef4444",
											}}
										>
											{myVote.inFavor ? "✓ You voted FOR" : "✗ You voted AGAINST"}
										</span>
									) : (
										<>
											<button
												type="button"
												className="btn btn-primary"
												style={{ fontSize: "0.85em" }}
												onClick={() => castVote(p.id, true)}
											>
												✓ Vote For
											</button>
											<button
												type="button"
												className="btn btn-secondary"
												style={{ fontSize: "0.85em", borderColor: "#ef4444", color: "#ef4444" }}
												onClick={() => castVote(p.id, false)}
											>
												✗ Vote Against
											</button>
										</>
									)
								) : null}

								<span
									style={{
										marginLeft: "auto",
										fontSize: "0.78em",
										color: "var(--muted)",
									}}
								>
									By {p.proposedBy} ·{" "}
									{p.status === "open"
										? `${daysLeft}d remaining`
										: new Date(p.createdAt).toLocaleDateString()}
								</span>
							</div>
						</div>
					</section>
				);
			})}

			{/* Info section on how governance works */}
			<section className="panel" style={{ marginTop: 20 }}>
				<div className="panel-header">
					<span>How Governance Works</span>
				</div>
				<div className="panel-body" style={{ fontSize: "0.88em" }}>
					<ul style={{ paddingLeft: 18, lineHeight: 1.7 }}>
						<li>
							<strong>Team Names:</strong> When you claim a franchise, you submit a
							name. The contract mints your NFT with that name marked "pending". A
							majority of other owners must approve for it to become official.
						</li>
						<li>
							<strong>Name Changes:</strong> Once per season, you may propose a new
							name. Requires an EMBR fee and majority vote. On approval the NFT
							metadata is updated on-chain.
						</li>
						<li>
							<strong>Relocations:</strong> Changing your city is a heavier process
							— larger EMBR fee, requires a supermajority (2/3 of owners).
						</li>
						<li>
							<strong>Rule Changes:</strong> Proposed by any owner. Requires
							majority to pass. Changes take effect the following season.
						</li>
						<li>
							<strong>Trades:</strong> Multi-team trades above a threshold (e.g. 3+
							players + picks) trigger a 48-hour league review window where any
							owner can call for a vote.
						</li>
						<li>
							<strong>Expansion:</strong> Requires a supermajority and a new
							franchise fee paid to the league treasury.
						</li>
					</ul>
				</div>
			</section>
		</>
	);
};
